import React, { useEffect, useMemo, useState } from 'react';
import { API_BASE_URL } from '../config';
import { apiGet, apiPost, apiPut, apiDelete } from '../utils/api';
import MeetingRoomOutlinedIcon from '@mui/icons-material/MeetingRoomOutlined';
import BuildOutlinedIcon from '@mui/icons-material/BuildOutlined';
import SpaOutlinedIcon from '@mui/icons-material/SpaOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import AttachMoneyOutlinedIcon from '@mui/icons-material/AttachMoneyOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ViewColumnOutlinedIcon from '@mui/icons-material/ViewColumnOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import { useSnackbar } from 'notistack';
import InlineFieldEditor from './admin/InlineFieldEditor';
import {
  ENTITIES,
  getAllListFieldKeys,
  getColumnLabel,
  getDefaultColumnKeys,
  isInlineEditable,
  isListReadOnly,
  loadVisibleColumnKeys,
  saveVisibleColumnKeys,
  formatCellDisplay,
  areFieldValuesEqual,
  formatMoneyInputValue,
  isMoneyField,
  roundMoney,
  buildRowSavePayload,
  buildTierPricingSavePayload,
  cleanFieldValueForApi,
  getAdminRowId,
  normalizeAdminRows,
  validateInlineField,
  exportAdminTableCsv,
  getFieldDef,
} from '../config/adminEntities';
import { getFieldSelectOptions } from '../config/adminFieldOptions';

/**
 * Get authorization headers for API calls
 */
const getAuthHeaders = () => {
  const token = localStorage.getItem('accessToken');
  return {
    'Content-Type': 'application/json',
    ...(token && { 'Authorization': `Bearer ${token}` })
  };
};

/**
 * Fetch with timeout and retry logic
 * @param {string} url - The URL to fetch
 * @param {object} options - Fetch options
 * @param {number} timeout - Timeout in milliseconds (default: 30000)
 * @param {number} retries - Number of retry attempts (default: 2)
 */
const fetchWithTimeout = async (url, options = {}, timeout = 30000, retries = 2) => {
  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), timeout);
      
      // Merge auth headers with any provided headers
      const headers = {
        ...getAuthHeaders(),
        ...(options.headers || {})
      };
      
      const response = await fetch(url, {
        ...options,
        headers,
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      return response;
    } catch (error) {
      if (attempt === retries) {
        // Last attempt failed
        if (error.name === 'AbortError') {
          throw new Error(`Request timed out after ${timeout}ms`);
        }
        throw error;
      }
      // Wait before retrying (exponential backoff)
      await new Promise(resolve => setTimeout(resolve, Math.pow(2, attempt) * 1000));
    }
  }
};

const Modal = ({ isOpen, title, children, onClose, wide = false }) => {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[9999] overflow-y-auto">
      <div 
        className={`bg-white rounded-lg p-6 w-full my-4 ${wide ? 'max-w-6xl' : 'max-w-2xl'}`}
        style={{ maxHeight: 'calc(100vh - 2rem)' }}
      >
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold">{title}</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700 text-2xl">✕</button>
        </div>
        <div style={{ maxHeight: 'calc(100vh - 10rem)', overflowY: 'auto' }}>
          {children}
        </div>
      </div>
    </div>
  );
};

const EntityForm = ({ entityKey, initialValues, onSubmit, onCancel, departments, roles, designations, treatmentTypes }) => {
  const { fields } = ENTITIES[entityKey];
  const [values, setValues] = useState(() => {
    const defaults = {};
    fields.forEach(f => {
      if (f.type === 'checkbox') {
        defaults[f.name] = f.name === 'is_active' ? true : false;
      } else {
        defaults[f.name] = '';
      }
    });
    return { ...defaults, ...initialValues };
  });

  const handleChange = (name, type, value, checked) => {
    setValues(v => ({ ...v, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Clean up values: convert empty strings to null for numeric fields
    const cleanedValues = {};
    fields.forEach(field => {
      cleanedValues[field.name] = cleanFieldValueForApi(field, values[field.name]);
    });
    
    onSubmit(cleanedValues);
  };

  // Get dynamic options for select fields (shared with inline editor)
  const getSelectOptions = (field, currentValue) =>
    getFieldSelectOptions(field, {
      departments,
      roles,
      designations,
      treatmentTypes,
      currentValue,
    });

  // Group fields for treatments
  const groupedTreatmentFields = entityKey === 'treatments' ? {
    'Basic Information': ['name', 'short_name', 'external_id', 'type', 'sub_category'],
    'Scheduling': ['duration', 'preparation_time', 'first_start_time', 'last_end_time', 'seq_no', 'allowed_start_times'],
    'Capacity & Requirements': ['therapists_required', 'max_guests', 'gender_specific', 'quantity'],
    'Pricing': ['base_price', 'tax_rate', 'tier_level', 'tier_value'],
    'Resources': ['products_used', 'equipment_used'],
    'Content': ['description', 'short_description', 'benefits', 'narration'],
    'Instructions & Compliance': ['booking_instructions', 'informed_consent', 'treatment_checklist'],
    'Media': ['image_url', 'thumbnail_url', 'background_url'],
  } : null;

  const renderField = (field) => {
    return (
      <div key={field.name} className={`flex items-center border-b border-gray-200 py-2.5 ${field.type === 'textarea' ? 'col-span-full' : ''}`}>
        <label className="text-sm text-gray-700 font-medium w-48 flex-shrink-0">
          {field.label}
          {field.required && <span className="text-red-500 ml-1">*</span>}
        </label>
        {field.type === 'checkbox' ? (
          <input
            type="checkbox"
            className="h-4 w-4 rounded border-gray-300 text-[#E4C984] focus:ring-[#E4C984]"
            checked={Boolean(values[field.name])}
            onChange={(e) => handleChange(field.name, field.type, null, e.target.checked)}
          />
        ) : field.type === 'select' ? (
          <select
            className="flex-1 bg-transparent px-2 py-1.5 text-sm font-semibold focus:outline-none"
            value={values[field.name] ?? ''}
            onChange={(e) => handleChange(field.name, field.type, e.target.value)}
            required={field.required}
          >
            <option value="">Select...</option>
            {getSelectOptions(field, values[field.name]).map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        ) : field.type === 'textarea' ? (
          <textarea
            className="flex-1 bg-transparent px-2 py-1.5 text-sm font-semibold focus:outline-none resize-y"
            value={values[field.name] ?? ''}
            onChange={(e) => handleChange(field.name, field.type, e.target.value)}
            required={field.required}
            rows={2}
            placeholder={`Enter ${field.label.toLowerCase()}...`}
          />
        ) : isMoneyField(field.name, field) ? (
          <input
            type="text"
            inputMode="decimal"
            className="flex-1 bg-transparent px-2 py-1.5 text-sm font-semibold focus:outline-none"
            value={
              values[field.name] === '' || values[field.name] == null
                ? ''
                : formatMoneyInputValue(values[field.name])
            }
            onChange={(e) => handleChange(field.name, field.type, e.target.value)}
            onBlur={(e) => {
              const rounded = roundMoney(e.target.value);
              if (rounded !== null) handleChange(field.name, field.type, rounded);
            }}
            required={field.required}
            placeholder="0.00"
          />
        ) : (
          <input
            type={field.type}
            className="flex-1 bg-transparent px-2 py-1.5 text-sm font-semibold focus:outline-none"
            value={values[field.name] ?? ''}
            onChange={(e) => handleChange(field.name, field.type, e.target.value)}
            required={field.required}
            placeholder={`Enter ${field.label.toLowerCase()}...`}
          />
        )}
      </div>
    );
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {entityKey === 'treatments' && groupedTreatmentFields ? (
        // Grouped layout for treatments with sections
        <div className="space-y-6 pr-2">
          {Object.entries(groupedTreatmentFields).map(([groupName, fieldNames]) => (
            <div key={groupName}>
              <h3 className="text-sm font-semibold text-gray-800 mb-3 uppercase tracking-wide border-b-2 pb-1" style={{ borderColor: '#E4C984' }}>
                {groupName}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8">
                {fieldNames.map(fieldName => {
                  const field = fields.find(f => f.name === fieldName);
                  return field ? renderField(field) : null;
                })}
              </div>
            </div>
          ))}
        </div>
      ) : (
        // Default 2-column layout for other entities
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8">
          {fields.map(field => renderField(field))}
        </div>
      )}
      <div className="flex justify-end gap-3 pt-3 border-t mt-4 bg-white">
        <button type="button" onClick={onCancel} className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50 text-sm font-medium">Cancel</button>
        <button type="submit" className="px-4 py-2 rounded-md hover:opacity-90 text-sm font-medium" style={{ backgroundColor: '#E4C984', color: '#171717' }}>Save</button>
      </div>
    </form>
  );
};

/** Assigned items first (A–Z), then unassigned items (A–Z). */
const normalizeMappingId = (id) => (id == null || id === '' ? '' : String(id));

const sortAssignedFirst = (items, assignedIds) => {
  const assignedSet = new Set(assignedIds.map(normalizeMappingId).filter(Boolean));
  return [...items].sort((a, b) => {
    const aOn = assignedSet.has(normalizeMappingId(a.id));
    const bOn = assignedSet.has(normalizeMappingId(b.id));
    if (aOn !== bOn) return aOn ? -1 : 1;
    return (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' });
  });
};

/** Assigned mappings first (A–Z), then remaining filtered items (A–Z). Keeps assigned visible when filters would hide them. */
const listAssignedFirst = (allItems, mappingsList, filterFn) => {
  const assignedIds = mappingsList.map((x) => x.id);
  const filtered = allItems.filter(filterFn);
  const filteredIdSet = new Set(filtered.map((s) => normalizeMappingId(s.id)));
  const assignedOnly = mappingsList
    .filter((m) => !filteredIdSet.has(normalizeMappingId(m.id)))
    .map((m) => {
      const full = allItems.find((item) => normalizeMappingId(item.id) === normalizeMappingId(m.id));
      return full || m;
    });
  return sortAssignedFirst([...assignedOnly, ...filtered], assignedIds);
};

const AdminPanel = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [entityKey, setEntityKey] = useState('rooms');
  const [items, setItems] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [refreshTick, setRefreshTick] = useState(0);
  const [showFilters, setShowFilters] = useState(false);
  const [columnFilters, setColumnFilters] = useState({});
  const [sortBy, setSortBy] = useState(null);
  const [sortDir, setSortDir] = useState('asc'); // 'asc' | 'desc'

  const entity = ENTITIES[entityKey];
  const [allRooms, setAllRooms] = useState([]);
  const [allEquipment, setAllEquipment] = useState([]);
  const [allTherapists, setAllTherapists] = useState([]);
  const [allTreatments, setAllTreatments] = useState([]);
  const [allDepartments, setAllDepartments] = useState([]);
  const [allRoles, setAllRoles] = useState([]);
  const [mappings, setMappings] = useState({ rooms: [], equipment: [], therapists: [] });
  const [activeMappingTab, setActiveMappingTab] = useState('rooms');
  const [rightSearch, setRightSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState(''); // Filter treatments by type
  const [roomTypeFilter, setRoomTypeFilter] = useState(''); // Filter rooms by cabin_type
  const [roleFilter, setRoleFilter] = useState(''); // Filter therapists by role
  const [designationFilter, setDesignationFilter] = useState(''); // Filter therapists by designation (e.g., Wellness Advisor)
  const [syncing, setSyncing] = useState(false);
  const [syncMessage, setSyncMessage] = useState(null);
  const [visibleColumnKeys, setVisibleColumnKeys] = useState(() => loadVisibleColumnKeys('rooms'));
  const [showColumnPicker, setShowColumnPicker] = useState(false);
  const [editingCell, setEditingCell] = useState(null); // { rowId, fieldName }
  const [savingCell, setSavingCell] = useState(null); // { rowId, fieldName }
  const [pendingEdits, setPendingEdits] = useState({}); // `${rowId}:${fieldName}` -> edit

  const pendingEditCount = Object.keys(pendingEdits).length;

  const visibleColumns = useMemo(
    () => visibleColumnKeys.map((key) => ({
      key,
      label: getColumnLabel(entityKey, key),
      field: getFieldDef(entityKey, key),
    })),
    [visibleColumnKeys, entityKey]
  );

  const stickyColumnKey = useMemo(() => {
    if (entityKey === 'tier_pricing') return 'tier_level';
    if (entityKey === 'therapists') return 'first_name';
    return 'name';
  }, [entityKey]);

  const addButtonLabel = useMemo(() => {
    switch (entityKey) {
      case 'rooms': return 'Add Room';
      case 'equipment': return 'Add Equipment';
      case 'treatments': return 'Add Service';
      case 'therapists': return 'Add Staff';
      default: return 'Add';
    }
  }, [entityKey]);

  // Dynamically extract unique treatment types from loaded data
  const treatmentTypes = useMemo(() => {
    const types = new Set();
    allTreatments.forEach(t => {
      if (t.type) {
        types.add(t.type);
      }
    });
    if (entityKey === 'treatments') {
      items.forEach(t => {
        if (t.type) {
          types.add(t.type);
        }
      });
    }
    return Array.from(types).sort();
  }, [allTreatments, items, entityKey]);

  // Extract unique room types
  const roomTypes = useMemo(() => {
    const types = new Set();
    allRooms.forEach(r => {
      if (r.cabin_type) {
        types.add(r.cabin_type);
      }
    });
    return Array.from(types).sort();
  }, [allRooms]);

  // Extract unique therapist roles
  const therapistRoles = useMemo(() => {
    const roles = new Set();
    allTherapists.forEach(t => {
      if (t.role) {
        roles.add(t.role);
      }
    });
    return Array.from(roles).sort();
  }, [allTherapists]);

  // Extract unique therapist designations
  const therapistDesignations = useMemo(() => {
    const designations = new Set();
    allTherapists.forEach(t => {
      if (t.designation) {
        designations.add(t.designation);
      }
    });
    if (entityKey === 'therapists') {
      items.forEach(t => {
        if (t.designation) {
          designations.add(t.designation);
        }
      });
    }
    return Array.from(designations).sort();
  }, [allTherapists, items, entityKey]);

  const filteredItems = useMemo(() => {
    // Global search filter
    const q = (search || '').toLowerCase();
    let rows = items.filter(row =>
      !q || Object.values(row).some(v => String(v ?? '').toLowerCase().includes(q))
    );

    // Column filters
    if (columnFilters && Object.keys(columnFilters).length > 0) {
      rows = rows.filter(row => {
        return Object.entries(columnFilters).every(([key, value]) => {
          if (value === undefined || value === null || value === '') return true;
          const cell = row[key];
          // boolean select handling
          if (value === 'true' || value === 'false') {
            return String(Boolean(cell)) === value;
          }
          return String(cell ?? '').toLowerCase().includes(String(value).toLowerCase());
        });
      });
    }

    // Sorting
    if (sortBy) {
      const dir = sortDir === 'asc' ? 1 : -1;
      const columnType = ENTITIES[entityKey].fields.find(f => f.name === sortBy)?.type || 'text';
      rows = [...rows].sort((a, b) => {
        const va = a[sortBy];
        const vb = b[sortBy];
        if (columnType === 'number') {
          const na = Number(va ?? 0);
          const nb = Number(vb ?? 0);
          return (na - nb) * dir;
        }
        if (columnType === 'checkbox') {
          const ba = va ? 1 : 0;
          const bb = vb ? 1 : 0;
          return (ba - bb) * dir;
        }
        return String(va ?? '').localeCompare(String(vb ?? ''), undefined, { sensitivity: 'base' }) * dir;
      });
    }

    return rows;
  }, [items, search, columnFilters, sortBy, sortDir, entityKey]);

  const fetchItems = async () => {
    try {
      setLoading(true);
      setError(null); // Clear any previous errors
      const res = await fetchWithTimeout(`${API_BASE_URL}/api/admin/${entity.path}`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to load data');
      setItems(normalizeAdminRows(entityKey, data));
    } catch (e) {
      console.error(`Error fetching ${entity.label}:`, e);
      setError(e.message || 'Failed to fetch data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchAllLookups = async () => {
    const safeJson = async (res, fallback = []) => {
      if (!res.ok) return fallback;
      try {
        const data = await res.json();
        return Array.isArray(data) ? data : fallback;
      } catch {
        return fallback;
      }
    };

    try {
      const [roomsRes, equipRes, therapistsRes, treatmentsRes, departmentsRes, rolesRes] = await Promise.all([
        fetchWithTimeout(`${API_BASE_URL}/api/admin/rooms`),
        fetchWithTimeout(`${API_BASE_URL}/api/admin/equipment`),
        fetchWithTimeout(`${API_BASE_URL}/api/admin/therapists`),
        fetchWithTimeout(`${API_BASE_URL}/api/admin/treatments`),
        fetchWithTimeout(`${API_BASE_URL}/api/admin/departments`),
        fetchWithTimeout(`${API_BASE_URL}/api/admin/roles`),
      ]);
      const [rooms, equipment, therapists, treatments, departments, roles] = await Promise.all([
        safeJson(roomsRes),
        safeJson(equipRes),
        safeJson(therapistsRes),
        safeJson(treatmentsRes),
        safeJson(departmentsRes),
        safeJson(rolesRes),
      ]);
      setAllRooms(rooms);
      setAllEquipment(equipment);
      setAllTherapists(therapists);
      setAllTreatments(treatments.map(t => ({ id: t.id, name: t.name, type: t.type })));
      setAllDepartments(departments);
      setAllRoles(roles);
    } catch (e) {
      console.error('Error fetching lookup data:', e);
    }
  };

  useEffect(() => {
    fetchItems();
    fetchAllLookups();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityKey, refreshTick]);

  // Clear selections when switching tabs
  useEffect(() => {
    setSelectedIds([]);
    setRightSearch('');
    setTypeFilter('');
    setRoomTypeFilter('');
    setRoleFilter('');
    setDesignationFilter('');
    setVisibleColumnKeys(loadVisibleColumnKeys(entityKey));
    setShowColumnPicker(false);
    setEditingCell(null);
    setSavingCell(null);
    setPendingEdits({});
  }, [entityKey]);

  const loadSelectionMappings = async () => {
    if (selectedIds.length === 0) return;
    try {
      setLoading(true);
      if (entityKey === 'treatments') {
        const res = await apiGet(`/api/admin/treatments/${selectedIds[0]}/mappings`);
        const data = await res.json();
        if (res.ok) {
          setMappings({ rooms: data.rooms || [], equipment: data.equipment || [], therapists: data.therapists || [] });
          setActiveMappingTab('rooms');
        }
      } else if (entityKey === 'rooms') {
        const svcRes = await apiGet(`/api/admin/rooms/${selectedIds[0]}/services`);
        const svcs = await svcRes.json();
        if (svcRes.ok) {
          setMappings({ rooms: [], equipment: [], therapists: (svcs || []).map(s => ({ id: s.id, name: s.name })) });
        }
      } else if (entityKey === 'equipment') {
        const res = await apiGet(`/api/admin/equipment/${selectedIds[0]}/services`);
        const data = await res.json();
        if (res.ok) {
          setMappings({ rooms: [], equipment: [], therapists: data || [] });
        }
      } else if (entityKey === 'therapists') {
        // Use new skills endpoint that includes expertise levels
        const res = await apiGet(`/api/admin/therapists/${selectedIds[0]}/skills`);
        const data = await res.json();
        if (res.ok) {
          setMappings({ rooms: [], equipment: [], therapists: (data || []).map(s => ({ 
            id: s.treatment_id ?? s.id, 
            name: s.treatment_name ?? s.name,
            expertise_level: s.expertise_level || 3  // Default to Competent
          })) });
        }
      }
    } catch (e) {
      // ignore auto-load errors
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Only load mappings if we have selections, to avoid race condition when switching tabs
    if (selectedIds.length > 0) {
      loadSelectionMappings();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedIds]); // Only depend on selectedIds, not entityKey

  const openCreate = () => {
    setEditingItem(null);
    setModalOpen(true);
  };

  const openEdit = (row) => {
    setEditingItem(row);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditingItem(null);
  };

  const inlineEditableColumns = useMemo(
    () => visibleColumns.filter((col) => isInlineEditable(entityKey, col.key)),
    [visibleColumns, entityKey]
  );

  const advanceEditCell = (direction, currentRowId, currentFieldName) => {
    const colKeys = inlineEditableColumns.map((c) => c.key);
    const rowIds = filteredItems.map((r) => r.id);
    const colIdx = colKeys.indexOf(currentFieldName);
    const rowIdx = rowIds.indexOf(currentRowId);

    if (direction === 'down') {
      if (rowIdx < rowIds.length - 1) {
        setEditingCell({ rowId: rowIds[rowIdx + 1], fieldName: currentFieldName });
      } else {
        setEditingCell(null);
      }
      return;
    }
    if (direction === 'next') {
      if (colIdx < colKeys.length - 1) {
        setEditingCell({ rowId: currentRowId, fieldName: colKeys[colIdx + 1] });
      } else {
        setEditingCell(null);
      }
      return;
    }
    if (direction === 'prev' && colIdx > 0) {
      setEditingCell({ rowId: currentRowId, fieldName: colKeys[colIdx - 1] });
    }
  };

  const stageInlineChange = (row, fieldName, newValue, advance) => {
    const field = getFieldDef(entityKey, fieldName);
    if (!field || !isInlineEditable(entityKey, fieldName)) return;

    const validation = validateInlineField(field, newValue);
    if (!validation.ok) {
      enqueueSnackbar(validation.message, { variant: 'error' });
      return;
    }

    const rowId = getAdminRowId(row, entityKey);
    const editKey = `${rowId}:${fieldName}`;
    const originalValue = pendingEdits[editKey]?.originalValue ?? row[fieldName];
    const cleaned = cleanFieldValueForApi(field, newValue);

    if (field.type !== 'checkbox' && areFieldValuesEqual(field, originalValue, cleaned)) {
      setPendingEdits((prev) => {
        const next = { ...prev };
        delete next[editKey];
        return next;
      });
      setItems((prev) =>
        prev.map((item) => (getAdminRowId(item, entityKey) === rowId ? { ...item, [fieldName]: originalValue } : item))
      );
      setEditingCell(null);
      if (advance) advanceEditCell(advance, rowId, fieldName);
      return;
    }

    setPendingEdits((prev) => ({
      ...prev,
      [editKey]: { rowId, fieldName, newValue: cleaned, originalValue },
    }));
    setItems((prev) =>
      prev.map((item) => (getAdminRowId(item, entityKey) === rowId ? { ...item, [fieldName]: cleaned } : item))
    );
    setEditingCell(null);
    if (advance) advanceEditCell(advance, rowId, fieldName);
  };

  const discardPendingEdits = () => {
    if (pendingEditCount === 0) return;
    setItems((prev) => {
      const next = prev.map((item) => ({ ...item }));
      Object.values(pendingEdits).forEach((edit) => {
        const idx = next.findIndex((i) => getAdminRowId(i, entityKey) === edit.rowId);
        if (idx >= 0) {
          next[idx] = { ...next[idx], [edit.fieldName]: edit.originalValue };
        }
      });
      return next;
    });
    setPendingEdits({});
    enqueueSnackbar('Unsaved changes discarded', { variant: 'info' });
  };

  const saveAllPendingEdits = async () => {
    if (pendingEditCount === 0) return;

    const rowIds = [...new Set(Object.values(pendingEdits).map((e) => e.rowId))];
    const changeCount = pendingEditCount;
    setLoading(true);
    setError(null);

    try {
      for (const rowId of rowIds) {
        const row = items.find((i) => getAdminRowId(i, entityKey) === rowId);
        if (!row) continue;
        setSavingCell({ rowId, fieldName: 'all' });

        if (entityKey === 'tier_pricing') {
          const payload = buildTierPricingSavePayload(row);
          const res = await apiPut(
            `/api/admin/tier-pricing/${encodeURIComponent(row.tier_level)}`,
            payload
          );
          const data = await res.json();
          if (!res.ok) throw new Error(data.detail || data.message || 'Update failed');
          setItems((prev) =>
            prev.map((item) =>
              getAdminRowId(item, entityKey) === rowId
                ? { ...item, tier_value: data.tier_value ?? payload.tier_value }
                : item
            )
          );
        } else {
          const payload = buildRowSavePayload(entityKey, row);
          const res = await apiPut(`/api/admin/${entity.path}/${rowId}`, payload);
          const data = await res.json();
          if (!res.ok) throw new Error(data.detail || 'Update failed');
          setItems((prev) =>
            prev.map((item) => (getAdminRowId(item, entityKey) === rowId ? { ...item, ...data } : item))
          );
        }
      }
      setPendingEdits({});
      enqueueSnackbar(`Saved ${changeCount} change(s)`, { variant: 'success' });
    } catch (e) {
      setError(e.message || 'Batch save failed');
      enqueueSnackbar(e.message || 'Batch save failed', { variant: 'error' });
    } finally {
      setSavingCell(null);
      setLoading(false);
    }
  };

  const handleExportCsv = () => {
    exportAdminTableCsv(entity.label, filteredItems, visibleColumnKeys, entityKey);
    enqueueSnackbar('CSV downloaded', { variant: 'success' });
  };

  const toggleVisibleColumn = (key) => {
    setVisibleColumnKeys((prev) => {
      const next = prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key];
      const normalized = next.length > 0 ? next : getDefaultColumnKeys(entityKey);
      saveVisibleColumnKeys(entityKey, normalized);
      return normalized;
    });
  };

  const resetVisibleColumns = () => {
    const defaults = getDefaultColumnKeys(entityKey);
    setVisibleColumnKeys(defaults);
    saveVisibleColumnKeys(entityKey, defaults);
  };

  const renderTableCell = (row, col, colIndex) => {
    const fieldName = col.key;
    const field = col.field || getFieldDef(entityKey, fieldName);
    const isSticky = fieldName === stickyColumnKey;
    const rowId = getAdminRowId(row, entityKey);
    const isEditing = editingCell?.rowId === rowId && editingCell?.fieldName === fieldName;
    const isSaving = savingCell?.rowId === rowId
      && (savingCell?.fieldName === fieldName || savingCell?.fieldName === 'all');
    const isPending = Boolean(pendingEdits[`${rowId}:${fieldName}`]);
    const isReadOnlyList = isListReadOnly(entityKey, fieldName);
    const canInline = isInlineEditable(entityKey, fieldName) && !modalOpen;

    const cellClass = [
      'px-3 py-1.5 align-top',
      colIndex === 0 ? 'font-semibold text-gray-900' : '',
      isSticky ? 'sticky left-0 z-[12] bg-white' : '',
      isPending ? 'bg-amber-50' : '',
      isReadOnlyList ? 'text-gray-500' : '',
      canInline && !isEditing ? 'cursor-pointer hover:bg-[#f6efd8]' : '',
    ].join(' ');

    if (isEditing && field) {
      return (
        <td key={fieldName} className={cellClass}>
          <InlineFieldEditor
            field={field}
            value={row[fieldName]}
            departments={allDepartments}
            roles={allRoles}
            designations={therapistDesignations}
            treatmentTypes={treatmentTypes}
            disabled={isSaving}
            onSave={(val, advance) => stageInlineChange(row, fieldName, val, advance)}
            onCancel={() => setEditingCell(null)}
          />
        </td>
      );
    }

    const display = formatCellDisplay(entityKey, fieldName, row[fieldName], row);
    const title = isReadOnlyList
      ? 'Read-only — edit in modal if needed'
      : canInline
        ? 'Click to edit'
        : display;

    return (
      <td
        key={fieldName}
        className={cellClass}
        title={title}
        onClick={() => {
          if (canInline) {
            setEditingCell({ rowId, fieldName });
          }
        }}
      >
        <span className={isSaving ? 'opacity-50' : ''}>
          {display}
          {isPending && <span className="ml-1 text-[10px] text-amber-700" title="Unsaved">●</span>}
        </span>
      </td>
    );
  };

  const handleSave = async (formValues) => {
    try {
      setLoading(true);
      setError(null);
      let payload = { ...formValues };
      if (entityKey === 'therapists' && !(payload.role || '').trim()) {
        const fallback = (payload.designation || payload.title || '').trim();
        if (fallback) {
          payload.role = fallback.replace(/\w\S*/g, (w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase());
        }
      }
      const path = editingItem ? `${entity.path}/${editingItem.id}` : entity.path;
      let res;
      if (entityKey === 'tier_pricing' && editingItem) {
        res = await apiPut(
          `/api/admin/tier-pricing/${encodeURIComponent(editingItem.tier_level)}`,
          buildTierPricingSavePayload(payload)
        );
      } else {
        res = editingItem
          ? await apiPut(`/api/admin/${path}`, payload)
          : await apiPost(`/api/admin/${path}`, payload);
      }
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Save failed');

      // Staff departure: apply roster cleanup + booking reassignment when deactivating
      if (entityKey === 'therapists' && editingItem) {
        const becameInactive = formValues.is_active === false;
        const hasExitDate = Boolean(formValues.exit_date);
        const statusChanged = becameInactive !== (editingItem.is_active !== false)
          || (formValues.exit_date || '') !== (editingItem.exit_date || '');
        if (statusChanged && (becameInactive || hasExitDate)) {
          const applyDeparture = window.confirm(
            `Apply departure for "${formValues.name || editingItem.name}"?\n\n` +
            `This will:\n` +
            `• Remove future roster assignments after their last working day\n` +
            `• Attempt to reassign their future bookings to other available staff\n\n` +
            `Past bookings are kept for records.`
          );
          if (applyDeparture) {
            const depRes = await apiPost(`/api/admin/therapists/${editingItem.id}/apply-departure`, {
              is_active: formValues.is_active !== false,
              exit_date: formValues.exit_date || null,
              reassign_future_bookings: true,
              remove_future_roster: true,
            });
            const depData = await depRes.json();
            if (!depRes.ok) {
              throw new Error(depData.detail || 'Departure processing failed');
            }
            const failed = depData.reassignment?.failed?.length || 0;
            const reassigned = depData.reassignment?.reassigned || 0;
            if (failed > 0) {
              setSyncMessage(
                `Staff saved. ${reassigned} booking(s) reassigned; ${failed} could not be auto-reassigned — review manually.`
              );
            } else if (reassigned > 0) {
              setSyncMessage(`Staff saved. ${reassigned} future booking(s) reassigned.`);
            }
          }
        }
      }

      setRefreshTick(t => t + 1);
      setPendingEdits({});
      closeModal();
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (row) => {
    if (!window.confirm(`Delete staff "${row.name}"?\n\nTip: For leaving staff, prefer setting Inactive + Exit Date instead of deleting.`)) return;
    try {
      setLoading(true);
      setError(null);

      const tryDelete = async (query = '') => {
        const res = await apiDelete(`/api/admin/${entity.path}/${row.id}${query}`);
        let data = {};
        try {
          data = await res.json();
        } catch (_) {
          data = {};
        }
        return { res, data };
      };

      let { res, data } = await tryDelete();
      if (res.ok) {
        setRefreshTick(t => t + 1);
        setSyncMessage(`Deleted staff "${row.name}".`);
        return;
      }

      const detail = data.detail || 'Delete failed';
      if (res.status === 400 && String(detail).toLowerCase().includes('booking')) {
        const reassignFirst = window.confirm(
          `${detail}\n\n` +
          `OK = Reassign FUTURE bookings to other staff, then delete if possible.\n` +
          `Cancel = Keep staff (edit Inactive + Exit Date instead).`
        );
        if (!reassignFirst) return;

        ({ res, data } = await tryDelete('?reassign_first=true'));
        if (res.ok) {
          const reassigned = data.reassignment?.reassigned || 0;
          setRefreshTick(t => t + 1);
          setSyncMessage(`Deleted "${row.name}". ${reassigned} future booking(s) reassigned.`);
          return;
        }

        const forceDelete = window.confirm(
          `${data.detail || detail}\n\n` +
          `Some bookings could not be reassigned (often past/completed visits).\n\n` +
          `DELETE ALL ${row.name}'s bookings and remove staff?\n` +
          `WARNING: This cannot be undone!`
        );
        if (!forceDelete) {
          setError(data.detail || 'Delete cancelled — staff still has bookings.');
          return;
        }

        ({ res, data } = await tryDelete('?force=true'));
        if (!res.ok) throw new Error(data.detail || 'Force delete failed');
        setRefreshTick(t => t + 1);
        setSyncMessage(`Deleted "${row.name}" and ${data.bookings_deleted || 0} booking(s).`);
        return;
      }

      throw new Error(detail);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSyncTreatments = async () => {
    if (!window.confirm('Sync treatments from external API? This will update existing treatments and add new ones.')) return;
    try {
      setSyncing(true);
      setSyncMessage(null);
      setError(null);
      
      const res = await apiPost('/api/admin/treatments/sync-external', {});
      const data = await res.json();
      
      if (!res.ok) throw new Error(data.detail || 'Sync failed');
      
      setSyncMessage(`✅ ${data.message} (${data.new} new, ${data.updated} updated)`);
      setRefreshTick(t => t + 1); // Refresh the list
      
      // Clear success message after 5 seconds
      setTimeout(() => setSyncMessage(null), 5000);
    } catch (e) {
      setError(e.message);
    } finally {
      setSyncing(false);
    }
  };

  const handleSyncTherapists = async () => {
    if (!window.confirm('Sync staff from external API? This will update existing staff and add new ones.')) return;
    try {
      setSyncing(true);
      setSyncMessage(null);
      setError(null);
      
      const res = await apiPost('/api/admin/therapists/sync-external', {});
      const data = await res.json();
      
      if (!res.ok) throw new Error(data.detail || 'Sync failed');
      
      setSyncMessage(`✅ ${data.message} (${data.new} new, ${data.updated} updated)`);
      setRefreshTick(t => t + 1); // Refresh the list
      
      // Clear success message after 5 seconds
      setTimeout(() => setSyncMessage(null), 5000);
    } catch (e) {
      setError(e.message);
    } finally {
      setSyncing(false);
    }
  };


  const toggleSort = (key) => {
    setSortBy(prev => {
      if (prev === key) {
        setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
        return prev;
      }
      setSortDir('asc');
      return key;
    });
  };

  const renderFilterInput = (col) => {
    const field = getFieldDef(entityKey, col.key);
    const type = field?.type || 'text';
    const value = columnFilters[col.key] ?? '';
    if (type === 'checkbox') {
      return (
        <select
          className="mt-1 block w-full rounded-md border-gray-300 bg-white shadow-sm focus:border-[#E4C984] focus:ring-1 sm:text-xs"
          value={value}
          onChange={(e) => setColumnFilters(f => ({ ...f, [col.key]: e.target.value }))}
        >
          <option value="">Any</option>
          <option value="true">Yes</option>
          <option value="false">No</option>
        </select>
      );
    }
    return (
      <input
        type={type === 'number' ? 'number' : 'text'}
        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#E4C984] focus:ring-1 sm:text-xs"
        value={value}
        onChange={(e) => setColumnFilters(f => ({ ...f, [col.key]: e.target.value }))}
        placeholder="Filter"
      />
    );
  };

  return (
    <div className="p-4 admin-panel-container" style={{ backgroundColor: '#f6f6f6' }}>
      <style>{`
        .admin-panel-container .text-sm {
          font-size: 0.7rem;
        }
      `}</style>
      <h3 className="text-lg font-semibold mb-3" style={{ color: '#171717' }}>Admin</h3>
      <div className="mb-4 flex items-center justify-between">
        <div className="flex gap-0 overflow-hidden rounded-md border" style={{ borderColor: '#E4C984' }}>
          {[
            { key: 'rooms', label: 'Rooms', Icon: MeetingRoomOutlinedIcon },
            { key: 'equipment', label: 'Equipment', Icon: BuildOutlinedIcon },
            { key: 'treatments', label: 'Services', Icon: SpaOutlinedIcon },
            { key: 'therapists', label: 'Staff', Icon: GroupsOutlinedIcon },
            { key: 'tier_pricing', label: 'Tier Pricing', Icon: AttachMoneyOutlinedIcon },
          ].map(({ key, label, Icon }, idx) => (
            <button
              key={key}
              onClick={() => setEntityKey(key)}
              className={`px-4 py-2 text-sm inline-flex items-center gap-2 ${entityKey === key ? 'bg-[#E4C984] text-[#171717]' : 'bg-white'}`}
              style={{ borderRight: idx < 4 ? '1px solid #E4C984' : undefined }}
              title={label}
            >
              <Icon fontSize="small" />
              {label}
            </button>
          ))}
        </div>
        <div className="flex gap-2">
          <input
            className="border rounded-md px-3 py-2 text-sm"
            placeholder={`Search ${entity.label.toLowerCase()}...`}
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          <button
            onClick={() => setShowColumnPicker(s => !s)}
            className={`px-3 py-2 text-sm rounded-md border flex items-center gap-1 ${showColumnPicker ? 'bg-[#f6efd8] border-[#E4C984]' : 'bg-white'}`}
            title="Choose visible columns"
          >
            <ViewColumnOutlinedIcon fontSize="small" />
            Columns
          </button>
          <button
            onClick={handleExportCsv}
            disabled={filteredItems.length === 0}
            className="px-3 py-2 text-sm rounded-md border bg-white flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            title="Download visible columns as CSV"
          >
            <FileDownloadOutlinedIcon fontSize="small" />
            Export
          </button>
          <button
            onClick={() => setShowFilters(s => !s)}
            className={`px-3 py-2 text-sm rounded-md border ${showFilters ? 'bg-[#f6efd8] border-[#E4C984]' : 'bg-white'}`}
          >
            {showFilters ? 'Hide Filters' : 'Show Filters'}
          </button>
          {entityKey === 'therapists' && (
            <button
              onClick={handleSyncTherapists}
              disabled={syncing}
              className="px-3 py-2 text-sm rounded-md border border-[#E4C984] bg-white hover:bg-[#f6efd8] disabled:opacity-50 disabled:cursor-not-allowed"
              title="Sync staff from AIIMS API - Updates AIIMS doctor IDs"
            >
              {syncing ? 'Syncing...' : 'Sync AIIMS'}
            </button>
          )}
          <button
            onClick={openCreate}
            className="px-3 py-2 text-sm rounded-md"
            style={{ backgroundColor: '#E4C984', color: '#171717' }}
          >
            {addButtonLabel}
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-3 p-3 bg-red-50 text-red-700 rounded flex items-center justify-between">
          <span>{error}</span>
          <button
            onClick={() => {
              setError(null);
              fetchItems();
            }}
            className="ml-4 px-3 py-1 text-sm rounded-md bg-red-100 hover:bg-red-200 text-red-800 border border-red-300"
          >
            Retry
          </button>
        </div>
      )}
      {syncMessage && (
        <div className="mb-3 p-3 bg-green-50 text-green-700 rounded">{syncMessage}</div>
      )}

      {showColumnPicker && (
        <div className="mb-3 p-3 bg-white border rounded-md" style={{ borderColor: '#e5e7eb' }}>
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-700">Visible columns</span>
            <button
              type="button"
              onClick={resetVisibleColumns}
              className="text-xs px-2 py-1 border rounded-md hover:bg-gray-50"
            >
              Reset to default
            </button>
          </div>
          <div className="flex flex-wrap gap-2">
            {getAllListFieldKeys(entityKey).map((key) => (
              <label
                key={key}
                className="inline-flex items-center gap-1.5 text-xs px-2 py-1 rounded border bg-gray-50 cursor-pointer"
              >
                <input
                  type="checkbox"
                  style={{ accentColor: '#E4C984' }}
                  checked={visibleColumnKeys.includes(key)}
                  onChange={() => toggleVisibleColumn(key)}
                />
                {getColumnLabel(entityKey, key)}
              </label>
            ))}
          </div>
          <p className="text-xs text-gray-500 mt-2">
            Click editable cells to change values. Tab moves across cells; Enter moves down.
            Use <strong>Save all</strong> when the bar appears. Use ✎ for full edit (all fields).
          </p>
        </div>
      )}

      <div className="overflow-auto border rounded-md bg-white flex" style={{ borderColor: '#e5e7eb', borderRadius: '16px', boxShadow: 'rgba(145, 158, 171, 0.12) 0px 12px 24px -4px, rgba(145, 158, 171, 0.2) 0px 0px 2px 0px' }}>
        <table className={`${selectedIds.length > 0 ? 'flex-1 w-0' : 'w-full'} text-sm`}>
          <thead>
            <tr className="bg-gray-50">
              <th className="px-3 py-2 sticky left-0 z-[14] bg-gray-50">
                <input
                  type="checkbox"
                  style={{ accentColor: '#E4C984' }}
                  checked={selectedIds.length > 0 && selectedIds.length === filteredItems.length}
                  onChange={(e) => setSelectedIds(e.target.checked ? filteredItems.map(r => r.id) : [])}
                />
              </th>
              {visibleColumns.map((col) => (
                <th
                  key={col.key}
                  className={`px-3 py-2 text-left font-semibold text-gray-700 select-none cursor-pointer ${col.key === stickyColumnKey ? 'sticky left-[42px] z-[13] bg-gray-50' : ''}`}
                  onClick={() => toggleSort(col.key)}
                  title="Sort"
                >
                  <span className="inline-flex items-center gap-1">
                    {col.label}
                    {sortBy === col.key && (
                      <span className="text-gray-400 text-xs">{sortDir === 'asc' ? '▲' : '▼'}</span>
                    )}
                  </span>
                </th>
              ))}
              <th className="px-3 py-2" />
            </tr>
            {showFilters && (
              <tr className="bg-white border-b">
                <th className="px-3 py-2 sticky left-0 z-[13] bg-white" />
                {visibleColumns.map((col) => (
                  <th
                    key={col.key}
                    className={`px-3 py-2 text-left font-normal ${col.key === stickyColumnKey ? 'sticky left-[42px] z-[12] bg-white' : ''}`}
                  >
                    {renderFilterInput(col)}
                  </th>
                ))}
                <th className="px-3 py-2 text-right">
                  <button
                    onClick={() => setColumnFilters({})}
                    className="px-2 py-1 text-xs border rounded-md"
                  >
                    Clear
                  </button>
                </th>
              </tr>
            )}
          </thead>
          <tbody>
            {loading ? (
              <tr><td className="px-3 py-4" colSpan={visibleColumns.length + 2}>Loading...</td></tr>
            ) : filteredItems.length === 0 ? (
              <tr><td className="px-3 py-4" colSpan={visibleColumns.length + 2}>No data</td></tr>
            ) : filteredItems.map(row => (
              <tr key={row.id} className="border-t">
                <td className="px-3 py-2 align-top sticky left-0 z-[12] bg-white">
                  <input
                    type="checkbox"
                    style={{ accentColor: '#E4C984' }}
                    checked={selectedIds.includes(row.id)}
                    onChange={(e) => setSelectedIds(prev => e.target.checked ? [...prev, row.id] : prev.filter(id => id !== row.id))}
                  />
                </td>
                {visibleColumns.map((col, idx) => renderTableCell(row, col, idx))}
                <td className="px-3 py-1.5 text-right whitespace-nowrap">
                  <button 
                    onClick={() => openEdit(row)} 
                    className="p-1 hover:bg-gray-100 rounded" 
                    style={{ color: '#cbb16f' }}
                    title="Edit all fields"
                  >
                    <EditOutlinedIcon fontSize="small" />
                  </button>
                  <button 
                    onClick={() => handleDelete(row)} 
                    className="ml-1 p-1 hover:bg-red-50 rounded text-red-600"
                    title="Delete"
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Right Mapping Panel: rendered only when selection exists */}
        {selectedIds.length > 0 && (
        <div className="w-[500px] border-l p-3 bg-gray-50 flex flex-col text-sm h-[calc(100vh-160px)]">
          {entityKey === 'treatments' && selectedIds.length > 0 && (
            <>
              {/* Summary of current assignments */}
              <div className="mb-2 p-2 bg-white border rounded-md flex gap-3 text-xs">
                <span className="font-medium text-gray-600">Assigned:</span>
                <span className={`px-2 py-0.5 rounded ${mappings.rooms.length > 0 ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                  {mappings.rooms.length} Rooms
                </span>
                <span className={`px-2 py-0.5 rounded ${mappings.equipment.length > 0 ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-500'}`}>
                  {mappings.equipment.length} Equipment
                </span>
                <span className={`px-2 py-0.5 rounded ${mappings.therapists.length > 0 ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-500'}`}>
                  {mappings.therapists.length} Staff
                </span>
              </div>
              
              {/* Tabs for Services section */}
              <div className="mb-0 flex border-b">
                {['rooms','equipment','therapists'].map(tab => {
                  const count = mappings[tab]?.length || 0;
                  return (
                    <button
                      key={tab}
                      onClick={() => {
                        setActiveMappingTab(tab);
                        // Clear filters when switching tabs
                        setRoomTypeFilter('');
                        setRoleFilter('');
                        setDesignationFilter('');
                        setRightSearch('');
                      }}
                      className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors flex items-center gap-1 ${
                        activeMappingTab === tab 
                          ? 'border-[#E4C984] text-[#171717] bg-white' 
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                    >
                      {tab.charAt(0).toUpperCase() + tab.slice(1)}
                      {count > 0 && (
                        <span className="ml-1 px-1.5 py-0.5 text-xs rounded-full bg-[#E4C984] text-[#171717]">
                          {count}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
              
              {/* Search and Filter - now under the tabs */}
              <div className="mt-3 mb-2">
                <div className="flex gap-2">
                  <input
                    className="flex-1 border rounded-md px-2 py-1 text-sm"
                    placeholder="Search..."
                    value={rightSearch}
                    onChange={(e) => setRightSearch(e.target.value)}
                  />
                  {/* Room type filter for services section - rooms tab */}
                  {activeMappingTab === 'rooms' && (
                    <select
                      className="border rounded-md px-2 py-1 text-sm"
                      style={{ minWidth: '150px' }}
                      value={roomTypeFilter}
                      onChange={(e) => setRoomTypeFilter(e.target.value)}
                    >
                      <option value="">All Room Types</option>
                      {roomTypes.map(type => (
                        <option key={type} value={type}>{type}</option>
                      ))}
                    </select>
                  )}
                  {/* Role filter for services section - therapists tab */}
                  {activeMappingTab === 'therapists' && (
                    <>
                      <select
                        className="border rounded-md px-2 py-1 text-sm"
                        style={{ minWidth: '150px' }}
                        value={roleFilter}
                        onChange={(e) => setRoleFilter(e.target.value)}
                      >
                        <option value="">All Roles</option>
                        {therapistRoles.map(role => (
                          <option key={role} value={role}>{role}</option>
                        ))}
                      </select>
                      <select
                        className="border rounded-md px-2 py-1 text-sm"
                        style={{ minWidth: '200px' }}
                        value={designationFilter}
                        onChange={(e) => setDesignationFilter(e.target.value)}
                      >
                        <option value="">All Designations</option>
                        {therapistDesignations.map(designation => (
                          <option key={designation} value={designation}>{designation}</option>
                        ))}
                      </select>
                    </>
                  )}
                </div>
              </div>

              {activeMappingTab === 'rooms' && (
                <div className="flex-1 overflow-auto border rounded-md p-2 bg-white">
                  {listAssignedFirst(allRooms, mappings.rooms, (r) => {
                    const matchesSearch = !rightSearch || (r.name || '').toLowerCase().includes(rightSearch.toLowerCase()) || (r.location || '').toLowerCase().includes(rightSearch.toLowerCase());
                    const matchesRoomType = !roomTypeFilter || (r.cabin_type || '') === roomTypeFilter;
                    return matchesSearch && matchesRoomType;
                  }).map(r => {
                    const selected = mappings.rooms.some(x => x.id === r.id);
                    return (
                      <label 
                        key={r.id} 
                        className={`flex items-center gap-2 py-1.5 px-2 rounded cursor-pointer transition-colors ${
                          selected 
                            ? 'bg-green-50 border border-green-200 hover:bg-green-100' 
                            : 'hover:bg-gray-50'
                        }`}
                      >
                        <input
                          type="checkbox"
                          style={{ accentColor: '#22c55e' }}
                          checked={selected}
                          onChange={(e) => {
                            setMappings(m => ({
                              ...m,
                              rooms: e.target.checked ? [...m.rooms, { id: r.id, name: r.name }] : m.rooms.filter(x => x.id !== r.id)
                            }));
                          }}
                        />
                        <span>{r.name}</span>
                        <span className="text-gray-500 text-xs">{r.location || ''}</span>
                      </label>
                    );
                  })}
                </div>
              )}

              {activeMappingTab === 'equipment' && (
                <div className="flex-1 overflow-auto border rounded-md p-2 bg-white space-y-1">
                  {listAssignedFirst(allEquipment, mappings.equipment, (e) =>
                    !rightSearch || (e.name || '').toLowerCase().includes(rightSearch.toLowerCase())
                  ).map(e => {
                    const existing = mappings.equipment.find(x => x.id === e.id);
                    const selected = Boolean(existing);
                    return (
                      <div 
                        key={e.id} 
                        className={`flex items-center gap-2 py-1.5 px-2 rounded transition-colors ${
                          selected 
                            ? 'bg-blue-50 border border-blue-200 hover:bg-blue-100' 
                            : 'hover:bg-gray-50'
                        }`}
                      >
                        <input
                          type="checkbox"
                          style={{ accentColor: '#3b82f6' }}
                          checked={selected}
                          onChange={(ev) => {
                            setMappings(m => ({
                              ...m,
                              equipment: ev.target.checked
                                ? [...m.equipment, { id: e.id, name: e.name, quantity: 1 }]
                                : m.equipment.filter(x => x.id !== e.id)
                            }));
                          }}
                        />
                        <span className="flex-1">{e.name}</span>
                        {selected && (
                          <input
                            type="number"
                            min={1}
                            value={existing?.quantity || 1}
                            className="w-20 border rounded px-2 py-1 text-sm bg-white"
                            onChange={(ev) => {
                              const qty = Number(ev.target.value || 1);
                              setMappings(m => ({
                                ...m,
                                equipment: m.equipment.map(x => x.id === e.id ? { ...x, quantity: qty } : x)
                              }));
                            }}
                          />
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {activeMappingTab === 'therapists' && (
                <div className="flex-1 overflow-auto border rounded-md p-2 bg-white space-y-1">
                  {listAssignedFirst(allTherapists, mappings.therapists, (t) => {
                    const matchesSearch = !rightSearch || (t.name || '').toLowerCase().includes(rightSearch.toLowerCase()) || (t.title || '').toLowerCase().includes(rightSearch.toLowerCase());
                    const matchesRole = !roleFilter || (t.role || '') === roleFilter;
                    const matchesDesignation = !designationFilter || (t.designation || '') === designationFilter;
                    return matchesSearch && matchesRole && matchesDesignation;
                  }).map(t => {
                    const selected = mappings.therapists.some(x => x.id === t.id);
                    return (
                      <label 
                        key={t.id} 
                        className={`flex items-center gap-2 py-1.5 px-2 rounded cursor-pointer transition-colors ${
                          selected 
                            ? 'bg-purple-50 border border-purple-200 hover:bg-purple-100' 
                            : 'hover:bg-gray-50'
                        }`}
                      >
                        <input
                          type="checkbox"
                          style={{ accentColor: '#a855f7' }}
                          checked={selected}
                          onChange={(e) => {
                            setMappings(m => ({
                              ...m,
                              therapists: e.target.checked ? [...m.therapists, { id: t.id, name: t.name }] : m.therapists.filter(x => x.id !== t.id)
                            }));
                          }}
                        />
                        <span className="flex-1">{t.name}</span>
                        <span className="text-gray-500 text-xs">{t.title || ''}</span>
                      </label>
                    );
                  })}
                </div>
              )}

              <div />
            </>
          )}

          {entityKey === 'rooms' && (
            <>
              {/* Summary for Rooms section */}
              <div className="mb-2 p-2 bg-white border rounded-md flex gap-3 text-xs">
                <span className="font-medium text-gray-600">Services assigned:</span>
                <span className={`px-2 py-0.5 rounded ${mappings.therapists.length > 0 ? 'bg-green-100 text-green-700 font-medium' : 'bg-gray-100 text-gray-500'}`}>
                  {mappings.therapists.length} services
                </span>
              </div>
              
              {/* Search and Filter for Rooms section */}
              <div className="mb-2">
                <div className="flex gap-2">
                  <input
                    className="flex-1 border rounded-md px-2 py-1 text-sm"
                    placeholder="Search..."
                    value={rightSearch}
                    onChange={(e) => setRightSearch(e.target.value)}
                  />
                  <select
                    className="border rounded-md px-2 py-1 text-sm"
                    style={{ minWidth: '150px' }}
                    value={typeFilter}
                    onChange={(e) => setTypeFilter(e.target.value)}
                  >
                    <option value="">All Types</option>
                    {treatmentTypes.map(type => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                </div>
              </div>
              
              <div className="flex-1 overflow-auto border rounded-md p-2 bg-white space-y-1">
                {listAssignedFirst(allTreatments, mappings.therapists, (svc) => {
                  const matchesSearch = !rightSearch || (svc.name || '').toLowerCase().includes(rightSearch.toLowerCase());
                  const matchesType = !typeFilter || (svc.type || '').toUpperCase() === typeFilter;
                  return matchesSearch && matchesType;
                }).map(svc => {
                  const selected = mappings.therapists.some(x => x.id === svc.id);
                  return (
                    <label 
                      key={svc.id} 
                      className={`flex items-center gap-2 py-1.5 px-2 rounded cursor-pointer transition-colors ${
                        selected 
                          ? 'bg-green-50 border border-green-200 hover:bg-green-100' 
                          : 'hover:bg-gray-50'
                      }`}
                    >
                      <input
                        type="checkbox"
                        style={{ accentColor: '#22c55e' }}
                        checked={selected}
                        onChange={(e) => {
                          setMappings(m => ({
                            ...m,
                            therapists: e.target.checked ? [...m.therapists, { id: svc.id, name: svc.name }] : m.therapists.filter(x => x.id !== svc.id)
                          }));
                        }}
                      />
                      <span className="flex-1">{svc.name}</span>
                    </label>
                  );
                })}
              </div>

              <div />
            </>
          )}

          {entityKey === 'equipment' && selectedIds.length > 0 && (
            <>
              {/* Summary for Equipment section */}
              <div className="mb-2 p-2 bg-white border rounded-md flex gap-3 text-xs">
                <span className="font-medium text-gray-600">Services using this equipment:</span>
                <span className={`px-2 py-0.5 rounded ${mappings.therapists.length > 0 ? 'bg-blue-100 text-blue-700 font-medium' : 'bg-gray-100 text-gray-500'}`}>
                  {mappings.therapists.length} services
                </span>
              </div>
              
              {/* Search and Filter for Equipment section */}
              <div className="mb-2">
                <div className="flex gap-2">
                  <input
                    className="flex-1 border rounded-md px-2 py-1 text-sm"
                    placeholder="Search..."
                    value={rightSearch}
                    onChange={(e) => setRightSearch(e.target.value)}
                  />
                  <select
                    className="border rounded-md px-2 py-1 text-sm"
                    style={{ minWidth: '150px' }}
                    value={typeFilter}
                    onChange={(e) => setTypeFilter(e.target.value)}
                  >
                    <option value="">All Types</option>
                    {treatmentTypes.map(type => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                </div>
              </div>
              
              <div className="flex-1 overflow-auto border rounded-md p-2 bg-white space-y-1">
                {listAssignedFirst(allTreatments, mappings.therapists, (svc) => {
                  const matchesSearch = !rightSearch || (svc.name || '').toLowerCase().includes(rightSearch.toLowerCase());
                  const matchesType = !typeFilter || (svc.type || '').toUpperCase() === typeFilter;
                  return matchesSearch && matchesType;
                }).map(svc => {
                  const existing = mappings.therapists.find(x => x.id === svc.id);
                  const selected = Boolean(existing);
                  return (
                    <div 
                      key={svc.id} 
                      className={`flex items-center gap-2 py-1.5 px-2 rounded transition-colors ${
                        selected 
                          ? 'bg-blue-50 border border-blue-200 hover:bg-blue-100' 
                          : 'hover:bg-gray-50'
                      }`}
                    >
                      <input
                        type="checkbox"
                        style={{ accentColor: '#3b82f6' }}
                        checked={selected}
                        onChange={(ev) => {
                          setMappings(m => ({
                            ...m,
                            therapists: ev.target.checked
                              ? [...m.therapists, { id: svc.id, name: svc.name, quantity: existing?.quantity || 1 }]
                              : m.therapists.filter(x => x.id !== svc.id)
                          }));
                        }}
                      />
                      <span className="flex-1">{svc.name}</span>
                      {selected && (
                        <input
                          type="number"
                          min={1}
                          value={existing?.quantity || 1}
                          className="w-20 border rounded px-2 py-1 text-sm bg-white"
                          onChange={(ev) => {
                            const qty = Number(ev.target.value || 1);
                            setMappings(m => ({
                              ...m,
                              therapists: m.therapists.map(x => x.id === svc.id ? { ...x, quantity: qty } : x)
                            }));
                          }}
                        />
                      )}
                    </div>
                  );
                })}
              </div>

              <div />
            </>
          )}

          {entityKey === 'therapists' && selectedIds.length > 0 && (
            <>
              {/* Summary for Staff section */}
              <div className="mb-2 p-2 bg-white border rounded-md flex gap-3 text-xs">
                <span className="font-medium text-gray-600">Skills assigned:</span>
                <span className={`px-2 py-0.5 rounded ${mappings.therapists.length > 0 ? 'bg-purple-100 text-purple-700 font-medium' : 'bg-gray-100 text-gray-500'}`}>
                  {mappings.therapists.length} services
                </span>
              </div>
              
              {/* Search and Filter for Therapists section */}
              <div className="mb-2">
                <div className="flex gap-2">
                  <input
                    className="flex-1 border rounded-md px-2 py-1 text-sm"
                    placeholder="Search..."
                    value={rightSearch}
                    onChange={(e) => setRightSearch(e.target.value)}
                  />
                  <select
                    className="border rounded-md px-2 py-1 text-sm"
                    style={{ minWidth: '150px' }}
                    value={typeFilter}
                    onChange={(e) => setTypeFilter(e.target.value)}
                  >
                    <option value="">All Types</option>
                    {treatmentTypes.map(type => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                </div>
              </div>
              
              <div className="flex-1 overflow-auto border rounded-md p-2 bg-white space-y-1">
                {listAssignedFirst(allTreatments, mappings.therapists, (svc) => {
                  const matchesSearch = !rightSearch || (svc.name || '').toLowerCase().includes(rightSearch.toLowerCase());
                  const matchesType = !typeFilter || (svc.type || '').toUpperCase() === typeFilter;
                  return matchesSearch && matchesType;
                }).map(svc => {
                  const existing = mappings.therapists.find(
                    (x) => normalizeMappingId(x.id) === normalizeMappingId(svc.id)
                  );
                  const selected = Boolean(existing);
                  const expertiseLevel = existing?.expertise_level || 3;
                  return (
                    <div 
                      key={svc.id} 
                      className={`flex items-center gap-2 py-1.5 px-2 rounded transition-colors ${
                        selected 
                          ? 'bg-purple-50 border border-purple-200 hover:bg-purple-100' 
                          : 'hover:bg-gray-50'
                      }`}
                    >
                      <input
                        type="checkbox"
                        style={{ accentColor: '#a855f7' }}
                        checked={selected}
                        onChange={(e) => {
                          setMappings(m => ({
                            ...m,
                            therapists: e.target.checked 
                              ? [...m.therapists, { id: svc.id, name: svc.name, expertise_level: 3 }] 
                              : m.therapists.filter(x => normalizeMappingId(x.id) !== normalizeMappingId(svc.id))
                          }));
                        }}
                      />
                      <span className="flex-1">{svc.name}</span>
                      {selected && (
                        <select
                          value={expertiseLevel}
                          className="border rounded px-2 py-1 text-sm bg-white"
                          style={{ width: '50px' }}
                          title="Expertise Level: 1=Expert, 2=Advanced, 3=Competent"
                          onChange={(e) => {
                            const level = Number(e.target.value);
                            setMappings(m => ({
                              ...m,
                              therapists: m.therapists.map(x => 
                                normalizeMappingId(x.id) === normalizeMappingId(svc.id) ? { ...x, expertise_level: level } : x
                              )
                            }));
                          }}
                        >
                          <option value={1}>1</option>
                          <option value={2}>2</option>
                          <option value={3}>3</option>
                        </select>
                      )}
                    </div>
                  );
                })}
              </div>

              <div />
            </>
          )}

          <div className="mt-3 border-t pt-2 bg-gray-50 sticky bottom-0">
            <button
              onClick={async () => {
                try {
                  setLoading(true);
                  if (entityKey === 'treatments') {
                    const payload = {
                      treatment_ids: selectedIds,
                      room_ids: mappings.rooms.map(r => r.id),
                      equipment: mappings.equipment.map(e => ({ equipment_id: e.id, quantity: Number(e.quantity || 1) })),
                      therapist_ids: mappings.therapists.map(t => t.id),
                    };
                    console.log('Sending bulk mappings payload:', payload);
                    const res = await apiPut('/api/admin/treatments/bulk/mappings', payload);
                    if (!res.ok) { 
                      const data = await res.json(); 
                      console.error('Bulk mappings error:', data);
                      throw new Error(data.detail || JSON.stringify(data) || 'Failed to save mappings'); 
                    }
                    console.log('Bulk mappings saved successfully');
                  } else if (entityKey === 'rooms') {
                    const serviceIds = mappings.therapists.map(s => s.id);
                    await Promise.all(selectedIds.map(async (roomId) => {
                      await apiPut(`/api/admin/rooms/${roomId}/services`, serviceIds);
                    }));
                  } else if (entityKey === 'equipment') {
                    await Promise.all(selectedIds.map(async (equipId) => {
                      await apiPut(`/api/admin/equipment/${equipId}/services`, mappings.therapists);
                    }));
                  } else if (entityKey === 'therapists') {
                    // Use new skills endpoint with expertise levels
                    const skills = mappings.therapists.map(s => ({ 
                      treatment_id: s.id, 
                      expertise_level: s.expertise_level || 3 
                    }));
                    await Promise.all(selectedIds.map(async (therapistId) => {
                      await apiPut(`/api/admin/therapists/${therapistId}/skills`, { skills });
                    }));
                  }
                  
                  // Show success message
                  const entityLabel = entityKey === 'treatments' ? 'services' : 
                                     entityKey === 'therapists' ? 'staff' : entityKey;
                  const assignedCount = mappings.rooms.length + mappings.equipment.length + mappings.therapists.length;
                  setSyncMessage(`✓ Successfully updated ${selectedIds.length} ${entityLabel} with ${assignedCount} assignment(s)`);
                  setTimeout(() => setSyncMessage(null), 4000);
                  
                  // Refresh the data to show updated counts
                  setRefreshTick(t => t + 1);
                } catch (e) {
                  setError(e.message);
                } finally {
                  setLoading(false);
                }
              }}
              className="w-full px-3 py-2 rounded-md"
              style={{ backgroundColor: '#E4C984', color: '#171717' }}
            >
              {entityKey === 'treatments' ? 'Apply to selected services' :
               entityKey === 'rooms' ? 'Apply to selected rooms' :
               entityKey === 'equipment' ? 'Apply to selected equipment' :
               'Apply to selected staff'}
            </button>
          </div>
        </div>
        )}
      </div>

      {pendingEditCount > 0 && (
        <div
          className="mt-3 flex items-center justify-between gap-3 px-4 py-3 rounded-md border bg-amber-50 border-amber-200"
          style={{ boxShadow: 'rgba(145, 158, 171, 0.12) 0px 4px 12px -2px' }}
        >
          <span className="text-sm text-amber-900 font-medium">
            {pendingEditCount} unsaved change{pendingEditCount !== 1 ? 's' : ''}
          </span>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={discardPendingEdits}
              className="px-3 py-1.5 text-sm rounded-md border bg-white hover:bg-gray-50"
            >
              Discard
            </button>
            <button
              type="button"
              onClick={saveAllPendingEdits}
              disabled={loading}
              className="px-3 py-1.5 text-sm rounded-md disabled:opacity-50"
              style={{ backgroundColor: '#E4C984', color: '#171717' }}
            >
              {loading ? 'Saving…' : 'Save all'}
            </button>
          </div>
        </div>
      )}

      <Modal 
        isOpen={modalOpen} 
        title={`${editingItem ? 'Edit' : 'Add'} ${entity.label.slice(0, -1)}`} 
        onClose={closeModal}
        wide={true}
      >
        <EntityForm
          entityKey={entityKey}
          initialValues={editingItem || {}}
          onSubmit={handleSave}
          onCancel={closeModal}
          departments={allDepartments}
          roles={allRoles}
          designations={therapistDesignations}
          treatmentTypes={treatmentTypes}
        />
      </Modal>
    </div>
  );
};

export default AdminPanel;


